import { ajv } from '@cibo/core/src/utils/ajv'
import type { JSONSchema7WithErrors } from '@cibo/core/src/types/JSONSchema7WithErrors'
import { path } from 'ramda'
import { useMemo } from 'react'

const isConditionalValueConfig = (
  obj: any
): obj is Array<{ schema?: JSONSchema7WithErrors; render: any }> =>
  Array.isArray(obj) && typeof obj[0]?.render !== 'undefined'

/**
 * Given a schema, a [{schema?,render}] list and a model (current value)...
 *
 * Returns the "render" value from the first item whose schema validates against the model.
 * If we reach an item with no schema, consider it a match.
 * If none match, return undefined.
 */
export const useConditionalValue = <T = any>({
  schema,
  configPath, // where this value is located in the schema
  model,
}: {
  schema?: JSONSchema7WithErrors
  configPath: string
  model: T
}) => {
  const valueConfig = path(configPath.split('.'), schema || {})

  const isConditional = isConditionalValueConfig(valueConfig)

  const rules = useMemo(
    () =>
      isConditional &&
      valueConfig?.map(({ schema: localSchema, render }) => {
        if (!localSchema) {
          return {
            schema: localSchema,
            validator: () => true,
            render,
          }
        }

        const finalSchema = localSchema?.$ref
          ? schema?.$defs?.[localSchema.$ref.replace('#/$defs/', '')]
          : localSchema

        if (!finalSchema) {
          throw new Error(`Could not find schema definition for ${localSchema}`)
        }

        const validator = ajv.compile(finalSchema)

        return {
          schema: localSchema,
          finalSchema,
          validator: (model: T) => {
            validator(model)
            return !validator.errors?.length
          },
          render,
        }
      }),
    [valueConfig]
  )

  if (!isConditional) return valueConfig

  if (!rules) return undefined

  for (const { validator, render } of rules) {
    if (validator(model)) {
      return render
    }
  }
}
